import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ClipboardList, Zap, TrendingUp, Trophy, AlertCircle, Sparkles, Check, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { TierBadge } from "@/components/TierBadge";
import { toast } from "sonner";

const TYPE_META = {
  fast_track_expiring: { icon: Zap, tone: "text-primary bg-primary/10" },
  close_to_legend: { icon: Trophy, tone: "text-amber-600 bg-amber-500/10" },
  close_to_pro_advanced: { icon: TrendingUp, tone: "text-purple-600 bg-purple-500/10" },
  one_away_from_pro: { icon: Sparkles, tone: "text-blue-600 bg-blue-500/10" },
  two_away_from_pro: { icon: Sparkles, tone: "text-blue-600 bg-blue-500/10" },
  close_to_reward_slab: { icon: TrendingUp, tone: "text-emerald-600 bg-emerald-500/10" },
  dormant: { icon: AlertCircle, tone: "text-muted-foreground bg-secondary/60" },
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "high", label: "High priority" },
  { id: "fast_track_expiring", label: "Fast Track" },
  { id: "close_to_legend", label: "→ Legend" },
  { id: "close_to_pro_advanced", label: "→ Pro Adv" },
  { id: "one_away_from_pro", label: "→ Pro" },
  { id: "close_to_reward_slab", label: "→ Slab" },
  { id: "dormant", label: "Dormant" },
];

function TaskCard({ task, onToggle, isDone }) {
  const meta = TYPE_META[task.type] || TYPE_META.dormant;
  const Icon = meta.icon;
  const toggleDone = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    await onToggle(task.task_key, !isDone);
  };
  return (
    <div
      data-testid={`task-${task.type}-${task.affiliate_id}`}
      data-completed={isDone ? "true" : "false"}
      className={`relative rounded-xl border p-4 transition-colors ${
        isDone
          ? "border-emerald-500/30 bg-emerald-500/5 opacity-70"
          : "border-border/60 hover:bg-secondary/40 hover:border-primary/30"
      }`}
    >
      <button
        data-testid={`task-toggle-${task.affiliate_id}`}
        onClick={toggleDone}
        title={isDone ? "Mark as not done" : "Mark as done"}
        className={`absolute top-3 right-3 h-6 w-6 rounded-full border flex items-center justify-center transition-all ${
          isDone
            ? "bg-emerald-500 border-emerald-500 text-white"
            : "border-border hover:border-primary hover:bg-primary/10"
        }`}
      >
        {isDone ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : <span className="sr-only">Mark done</span>}
      </button>
      <Link to={`/affiliates/${task.affiliate_id}`} className="block">
        <div className="flex items-start gap-3 pr-8">
          <div
            className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${meta.tone}`}
          >
            <Icon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={`text-sm font-semibold truncate ${isDone ? "line-through" : ""}`}>{task.name}</span>
              <TierBadge tier={task.tier} />
              {task.priority === 1 && !isDone && (
                <span className="text-[9px] uppercase font-bold tracking-widest text-destructive">
                  High
                </span>
              )}
            </div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">
              {task.title}
            </div>
            <div className="text-xs text-foreground/80">{task.message}</div>
            {isDone && task.completed_by && (
              <div className="mt-2 text-[10px] uppercase tracking-widest text-emerald-600 dark:text-emerald-400 font-semibold">
                ✓ Done by {task.completed_by}
                {task.completed_at && ` · ${new Date(task.completed_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`}
              </div>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
}

export function WeeklyTasks({ asmId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");
  const [showDone, setShowDone] = useState(false);
  const [pending, setPending] = useState(new Set());

  const load = () => {
    setError(false);
    api
      .get("/dashboard/tasks", { params: asmId ? { asm_id: asmId } : {} })
      .then((r) => setData(r.data))
      .catch(() => {
        setData({ tasks: [], count: 0, completed_count: 0 });
        setError(true);
      });
  };

  useEffect(load, [asmId]);

  const toggle = async (task_key, done) => {
    // optimistic update
    if (pending.has(task_key)) return;
    setPending((s) => new Set(s).add(task_key));
    setData((d) => ({
      ...d,
      tasks: d.tasks.map((t) => (t.task_key === task_key ? { ...t, completed: done } : t)),
      completed_count: (d.completed_count || 0) + (done ? 1 : -1),
    }));
    try {
      const url = done ? "/dashboard/tasks/complete" : "/dashboard/tasks/uncomplete";
      await api.post(url, { task_key });
    } catch (e) {
      toast.error("Could not update task");
      // revert
      setData((d) => ({
        ...d,
        tasks: d.tasks.map((t) => (t.task_key === task_key ? { ...t, completed: !done } : t)),
        completed_count: (d.completed_count || 0) + (done ? -1 : 1),
      }));
    } finally {
      setPending((s) => {
        const n = new Set(s);
        n.delete(task_key);
        return n;
      });
    }
  };

  if (!data) {
    return (
      <div className="rounded-2xl border border-border/60 bg-card p-6">
        <div className="text-sm text-muted-foreground">Loading tasks…</div>
      </div>
    );
  }

  const openTasks = data.tasks.filter((t) => !t.completed);
  const doneTasks = data.tasks.filter((t) => t.completed);

  const filteredOpen = openTasks.filter((t) => {
    if (filter === "all") return true;
    if (filter === "high") return t.priority === 1;
    if (filter === "one_away_from_pro") return t.type === "one_away_from_pro" || t.type === "two_away_from_pro";
    return t.type === filter;
  });

  const counts = FILTERS.reduce((acc, f) => {
    acc[f.id] =
      f.id === "all"
        ? openTasks.length
        : f.id === "high"
        ? openTasks.filter((t) => t.priority === 1).length
        : f.id === "one_away_from_pro"
        ? openTasks.filter((t) => t.type === "one_away_from_pro" || t.type === "two_away_from_pro").length
        : openTasks.filter((t) => t.type === f.id).length;
    return acc;
  }, {});

  return (
    <div data-testid="weekly-tasks" className="rounded-2xl border border-border/60 bg-card p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold tracking-tight">Weekly Tasks</h3>
          <span className="text-xs text-muted-foreground">
            · {openTasks.length} open · {doneTasks.length} done this week
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`task-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`text-xs rounded-full px-3 py-1 border transition-colors ${
                filter === f.id
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border/60 hover:bg-secondary"
              }`}
            >
              {f.label} <span className="opacity-60">{counts[f.id] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-3 text-xs rounded-lg border border-destructive/30 bg-destructive/5 text-destructive px-3 py-2">
          Could not load tasks. Try refreshing the page.
        </div>
      )}

      {filteredOpen.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">
          Nothing here — you&apos;re all caught up.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 max-h-[420px] overflow-y-auto pr-1">
          {filteredOpen.map((t) => (
            <TaskCard key={t.task_key} task={t} onToggle={toggle} isDone={false} />
          ))}
        </div>
      )}

      {doneTasks.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border/60">
          <button
            data-testid="tasks-done-toggle"
            onClick={() => setShowDone((v) => !v)}
            className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {showDone ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            Done this week ({doneTasks.length})
            <span className="text-[11px] text-muted-foreground">
              — resets on Monday
            </span>
          </button>
          {showDone && (
            <div data-testid="tasks-done-list" className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {doneTasks.map((t) => (
                <TaskCard key={t.task_key} task={t} onToggle={toggle} isDone={true} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
